import { obterArquivoAnexo } from "./anexoCache";
import { normalizarDescricao } from "./anexosStaged";
import { ehImagemAssinavel, imagemParaPdf } from "./imagemParaPdf";
import { ehArquivoPdf, mesclarPdfs } from "./mesclarPdfs";
import { rotacionarPdf } from "./rotacao";

/**
 * Baixa os anexos de uma solicitação na ordem das descrições informadas (ex.: ["Contrato",
 * "Documento do Responsável", "Comprovante de Endereço"]), prontos para virar um PDF único. Cada item da
 * ordem pode ser uma lista de descrições equivalentes — a primeira é o nome exibido (ex.: ["Contrato",
 * "Contrato Assinado"] para aceitar o nome antigo). Vários anexos do mesmo tipo vêm juntos, na ordem em que
 * foram enviados. Só PDFs e imagens entram; os demais tipos (Word, planilha...) vão para `ignorados`.
 *
 * @returns {Promise<{ itens: Array<{ id, descricao: string, nome: string, tipo: "pdf"|"imagem", blob: Blob }>,
 *                    faltando: string[], ignorados: string[] }>}
 */
export async function carregarAnexosParaPdf(anexos = [], ordemDescricoes = []) {
    const ordenados = [...anexos].sort((a, b) => String(a.dataHora || "").localeCompare(String(b.dataHora || "")));
    const itens = [];
    const faltando = [];
    const ignorados = [];

    for (const item of ordemDescricoes) {
        const equivalentes = (Array.isArray(item) ? item : [item]).map(d => normalizarDescricao(d));
        const descricao = Array.isArray(item) ? item[0] : item;
        const doTipo = ordenados.filter(a => equivalentes.includes(normalizarDescricao(a.descricao)));
        if (doTipo.length === 0) {
            faltando.push(descricao);
            continue;
        }
        for (const anexo of doTipo) {
            const nome = anexo.nomeOriginal || descricao;
            const ehPdf = ehArquivoPdf({ type: anexo.contentType, name: nome });
            if (!ehPdf && !ehImagemAssinavel(nome)) {
                ignorados.push(nome);
                continue;
            }
            const { blob } = await obterArquivoAnexo(anexo);
            itens.push({ id: anexo.id, descricao, nome, tipo: ehPdf ? "pdf" : "imagem", blob });
        }
    }
    return { itens, faltando, ignorados };
}

/**
 * Monta o PDF único a partir dos itens de {@link carregarAnexosParaPdf}, na ordem recebida. Cada imagem vira
 * uma página (o `blob` pode já vir girado/recortado); PDFs com `rotacao` (90/180/270) têm as páginas giradas.
 * Devolve `null` se não houver itens.
 */
export async function montarPdfUnico(itens = [], nomeSaida = "documentos.pdf") {
    if (itens.length === 0) return null;
    const arquivos = [];
    for (const item of itens) {
        let pdf;
        if (item.tipo === "pdf") pdf = item.rotacao ? await rotacionarPdf(item.blob, item.rotacao) : item.blob;
        else pdf = await imagemParaPdf(item.blob);
        arquivos.push(new File([pdf], item.nome, { type: "application/pdf" }));
    }
    return mesclarPdfs(arquivos, nomeSaida);
}

/**
 * Atalho: carrega e junta tudo de uma vez, sem edição.
 *
 * @returns {Promise<{ arquivo: File|null, incluidos: string[], faltando: string[], ignorados: string[] }>}
 */
export async function juntarAnexosEmPdf(anexos = [], ordemDescricoes = [], nomeSaida = "documentos.pdf") {
    const { itens, faltando, ignorados } = await carregarAnexosParaPdf(anexos, ordemDescricoes);
    const arquivo = await montarPdfUnico(itens, nomeSaida);
    return { arquivo, incluidos: itens.map(i => i.nome), faltando, ignorados };
}

/** Baixa um File no navegador. */
export function baixarArquivo(arquivo) {
    const url = URL.createObjectURL(arquivo);
    const link = document.createElement("a");
    link.href = url;
    link.download = arquivo.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
}
